import { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Autocomplete,
  TextField,
  Grid,
  Stack,
  IconButton,
  Tooltip,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";

import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import AddIcon from "@mui/icons-material/Add";
import { apiUrl } from "config/envConfig";

import { getDrivers } from "services/driverService";
import { getAllCashOutRequests, createCashOutRequest, approveCashOutRequest, rejectCashOutRequest } from "services/cashOutRequest";
import { getCashOutRule } from "services/cashOutRule";
import { getAllWallets } from "services/walletManagement";
import { getBlobFromUrl, getBlobByFileName } from "services/fileService";
import CashOutRequestForm, { paymentMethodEnum } from "Components/CashOutRequest/CashOutRequestForm";
import FileUploader from "Components/FileUploader/FileUploader";
import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE } from "constants/paginationConstants";

// Local status labels and colors for cash out requests (use backend TransactionStatusEnum values)
// TransactionStatusEnum: Pending=1, Success=2, Failed=3, Reversed=4, Approved=5, Rejected=6, ...
const cashOutStatusEnumLocal = {
  1: "Pending",
  5: "Approved",
  6: "Rejected",
};

const statusColors = {
  1: "#FFA500",
  5: "#4CAF50",
  6: "#F44336",
};

const CashOutRequestPage = () => {

  const [requests, setRequests] = useState([]);
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    driverId: "",
    status: "",
    fromDate: "",
    toDate: "",
  });

  // Driver search input state
  const [driverInputValue, setDriverInputValue] = useState("");

  // Drivers list for filter dropdown
  const [drivers, setDrivers] = useState([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  // sentinel option representing no driver filter (show all drivers)
  const ALL_DRIVERS_OPTION = { id: "__ALL__", fullName: "All Drivers", phone: "" };

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState("view"); // view, approve, reject, create
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [approvePaymentMethod, setApprovePaymentMethod] = useState("");
  const [approveFiles, setApproveFiles] = useState([]);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [approveUploadProgress, setApproveUploadProgress] = useState(null);
  const [createFormData, setCreateFormData] = useState({
    walletId: "",
    amount: 0,
    paymentMethod: "",
    files: [],
  });
  const [createUploadProgress, setCreateUploadProgress] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [walletsLoading, setWalletsLoading] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Snackbar state for notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // success, error, warning, info
  });

  const driverSearchRefLocal = useRef(null);

  // Fetch drivers for filter dropdown
  const fetchDrivers = async () => {
    setDriversLoading(true);
    try {
      // Fetch both Regular and Expedited drivers and merge, to ensure
      // expedited drivers are included even if backend filters by type.
      const [r1, r2] = await Promise.all([
        getDrivers(1000, 1, "", "", "", [], null, null, null, null, null, 1),
        getDrivers(1000, 1, "", "", "", [], null, null, null, null, null, 2),
      ]);
      const a = r1?.page || [];
      const b = r2?.page || [];
      const combined = [...a];
      const ids = new Set(a.map((d) => String(d.id)));
      for (const d of b) {
        if (!ids.has(String(d.id))) {
          combined.push(d);
          ids.add(String(d.id));
        }
      }
      setDrivers(combined);
    } catch (err) {
      console.error("Failed to fetch drivers", err);
      setDrivers([]);
    } finally {
      setDriversLoading(false);
    }
  };

  // Debounced remote search when user types in the driver Autocomplete
  const searchDrivers = (term) => {
    if (driverSearchRefLocal.current) clearTimeout(driverSearchRefLocal.current);
    driverSearchRefLocal.current = setTimeout(async () => {
      setDriversLoading(true);
      try {
        // search both driver types
        const [r1, r2] = await Promise.all([
          getDrivers(100, 1, term, "", "", [], null, null, null, null, null, 1),
          getDrivers(100, 1, term, "", "", [], null, null, null, null, null, 2),
        ]);
        const a = r1?.page || [];
        const b = r2?.page || [];
        const combined = [...a];
        const ids = new Set(a.map((d) => String(d.id)));
        for (const d of b) {
          if (!ids.has(String(d.id))) {
            combined.push(d);
            ids.add(String(d.id));
          }
        }
        // Ensure the currently selected driver stays in the options so the
        // Autocomplete can render the selection even if server results
        // don't include it.
        if (selectedDriver && !combined.some((d) => String(d.id) === String(selectedDriver.id))) {
          setDrivers([selectedDriver, ...combined]);
        } else {
          setDrivers(combined || []);
        }
      } catch (e) {
        console.error('Driver search failed', e);
      } finally {
        setDriversLoading(false);
      }
    }, 300);
  };

  // Fetch requests (accept optional paging params and filter overrides for callers that want immediate refresh)
  const fetchRequests = async (pageParam = page, rowsParam = rowsPerPage, overrides = {}) => {
    setLoading(true);
    try {
      // allow callers to pass explicit overrides so callers (like Reset) can force the API
      // call to use the cleared filter values immediately without waiting for state updates
      const overrideFilters = overrides.filters || null;
      const overrideSelectedDriver = overrides.selectedDriver || null;

      const resolvedDriverId = overrideSelectedDriver?.id
        ? String(overrideSelectedDriver.id)
        : overrideFilters && overrideFilters.driverId
        ? (overrideFilters.driverId === "" ? null : String(overrideFilters.driverId))
        : selectedDriver?.id
        ? String(selectedDriver.id)
        : filters.driverId
        ? String(filters.driverId)
        : null;

      const status = overrideFilters && typeof overrideFilters.status !== 'undefined'
        ? (overrideFilters.status ? parseInt(overrideFilters.status) : null)
        : (filters.status ? parseInt(filters.status) : null);

      const fromDate = overrideFilters && typeof overrideFilters.fromDate !== 'undefined' ? overrideFilters.fromDate : filters.fromDate;
      const toDate = overrideFilters && typeof overrideFilters.toDate !== 'undefined' ? overrideFilters.toDate : filters.toDate;

      const response = await getAllCashOutRequests({
        pageNumber: pageParam,
        pageSize: rowsParam,
        driverId: resolvedDriverId,
        status,
        fromDate,
        toDate,
      });

      // response is normalized to { items, totalCount }
      setRequests(response.items || []);
      setTotalCount(response.totalCount || 0);
    } catch (err) {
      console.error("Failed to fetch cash out requests", err);
      setRequests([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    fetchDrivers(); // Fetch drivers on component mount
  }, [page, rowsPerPage]);

  // Sync selectedDriver with filters.driverId and drivers list
  useEffect(() => {
    if (!filters.driverId) {
      setSelectedDriver(null);
      return;
    }
    const found = drivers.find((d) => String(d.id) === String(filters.driverId));
    setSelectedDriver(found || null);
  }, [drivers, filters.driverId]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prevFilters) => ({ ...prevFilters, [name]: value }));
  };

  const handleApplyFilters = () => {
    // Ensure filters reflect the currently selected driver before fetching
    setFilters((prev) => ({
      ...prev,
      driverId: selectedDriver?.id ? String(selectedDriver.id) : prev.driverId,
    }));
    setPage(DEFAULT_PAGE);
    fetchRequests();
  };

  const handleResetFilters = () => {
    const cleared = {
      driverId: "",
      status: "",
      fromDate: "",
      toDate: "",
    };

    setFilters(cleared);
    setDriverInputValue("");
    setSelectedDriver(null);
    setRowsPerPage(DEFAULT_ROWS_PER_PAGE);
    setPage(DEFAULT_PAGE);
    // Immediately refresh using explicit cleared filters so API sees the reset
    fetchRequests(DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE, { filters: cleared, selectedDriver: null });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(DEFAULT_PAGE);
  };

  const handleOpenViewDialog = (request) => {
    setSelectedRequest(request);
    setDialogMode("view");
    setOpenDialog(true);
  };

  const handleOpenApproveDialog = (request) => {
    setSelectedRequest(request);
    setDialogMode("approve");
    setApprovePaymentMethod("");
    setOpenDialog(true);
  };

  const handleOpenRejectDialog = (request) => {
    setSelectedRequest(request);
    setDialogMode("reject");
    setRejectReason("");
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRequest(null);
    setRejectReason("");
    setApprovePaymentMethod("");
    setCreateFormData({
      walletId: "",
      amount: 0,
      paymentMethod: "",
      files: [],
    });
    setSelectedWallet(null);
  };

  const handleOpenCreateDialog = async () => {
    setDialogMode("create");
    setCreateFormData({
      walletId: "",
      amount: 0,
      paymentMethod: "",
      files: [],
    });
    setSelectedWallet(null);
    setWalletsLoading(true);
    try {
      const data = await getAllWallets();
      // `getAllWallets` returns a pagination object from the API.
      // Normalize to an array for the create dialog: prefer `items` or `page` fields.
      const list = (data && (data.items || data.page || data.list)) || [];
      setWallets(list);
    } catch (err) {
      console.error("Failed to load wallets", err);
      setWallets([]);
    } finally {
      setWalletsLoading(false);
    }
    setOpenDialog(true);
  };

  const handleApprove = async () => {
    setDialogLoading(true);
    try {
      // Backend handles the status change, pass payment method and optional files
      const onUploadProgress = (progressEvent) => {
        if (!progressEvent || !progressEvent.total) return;
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setApproveUploadProgress(percent);
      };

      // Debug: log what we are sending so we can trace binding issues on the server
      if (process.env.NODE_ENV !== 'production') {
        try {
          // eslint-disable-next-line no-console
          console.debug('Approving cash out:', { id: selectedRequest.id, paymentMethod: approvePaymentMethod, filesCount: (approveFiles || []).length });
        } catch (e) {}
      }

      await approveCashOutRequest(selectedRequest.id, approvePaymentMethod || 1, approveFiles, onUploadProgress);
      const withEvidence = approveFiles && approveFiles.length > 0;
      setSnackbar({
        open: true,
        message: withEvidence
          ? "✅ Cash out request approved successfully (with attached admin evidence)!"
          : "✅ Cash out request approved successfully!",
        severity: "success",
      });
      // clear attached files after approval
      setApproveFiles([]);
      setApproveUploadProgress(null);
      handleCloseDialog();
      fetchRequests();
    } catch (err) {
      console.error("Failed to approve", err);
      const { code, message: backendMessage } = extractErrorInfo(err);
      // try building message (handles CashOutNotAllowed etc.)
      let display = await buildCashOutUserMessage(code, backendMessage).catch(() => null);
      if (!display) display = mapCashOutError(code, backendMessage) || (backendMessage ? `❌ ${backendMessage}` : null);

      if (!display) {
        const raw = err?.response?.data;
        if (raw) {
          if (typeof raw === "string") display = `❌ ${raw}`;
          else if (typeof raw === "object") {
            const vals = Object.values(raw).flatMap((v) => (Array.isArray(v) ? v : [v]));
            const found = vals.find((v) => typeof v === "string" && v.trim().length > 3);
            if (found) display = `❌ ${found}`;
            else {
              try {
                display = `❌ ${JSON.stringify(raw).slice(0,400)}`;
              } catch (e) {
                display = `❌ ${String(raw)}`;
              }
            }
          }
        }
      }

      if (!display) display = `❌ ${err?.message || "Failed to approve request"}`;

      // In dev, print the raw response to console for debugging so you can
      // copy the exact response body if needed.
      if (process.env.NODE_ENV !== 'production') {
        try {
          // eslint-disable-next-line no-console
          console.debug('CashOut approve error response raw:', err?.response?.data || err);
        } catch (e) {}
      }

      setSnackbar({ open: true, message: display, severity: "error" });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      setSnackbar({
        open: true,
        message: "⚠️ Please provide a rejection reason",
        severity: "warning",
      });
      return;
    }

    setDialogLoading(true);
    try {
      await rejectCashOutRequest(selectedRequest.id, rejectReason);
      setSnackbar({
        open: true,
        message: "✅ Cash out request rejected successfully!",
        severity: "success",
      });
      handleCloseDialog();
      fetchRequests();
    } catch (err) {
      console.error("Failed to reject", err);
      const errorMsg = err.response?.data?.message || "Failed to reject request";
      setSnackbar({
        open: true,
        message: "❌ " + errorMsg,
        severity: "error",
      });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleApproveFilesChange = (e) => {
    const { files } = e.target;
    if (files && files.length) {
      setApproveFiles(Array.from(files));
    }
  };

  const removeApproveFile = (index) => {
    setApproveFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCreateFormChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      setCreateFormData((prev) => ({
        ...prev,
        files: Array.from(files),
      }));
    } else {
      setCreateFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setCreateFormData((prev) => ({
        ...prev,
        files: Array.from(e.dataTransfer.files),
      }));
    }
  };

  const handleRemoveFile = (index) => {
    setCreateFormData((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
  };

  const getFileIcon = (filename) => {
    return <InsertDriveFileIcon sx={{ fontSize: "1rem", mr: 0.5 }} />;
  };

  // Human-readable file size
  const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(i === 0 ? 0 : 2)} ${sizes[i]}`;
  };

  // Preview state and helpers
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const getExtension = (name) => {
    if (!name) return "";
    const parts = name.split(".");
    return parts.length > 1 ? parts.pop().toLowerCase() : "";
  };

  const isPreviewable = (f) => {
    const ext = getExtension(f.fileName || f.FileName || f.fileId || f.FileId || "");
    return ["png", "jpg", "jpeg", "gif", "webp", "svg", "pdf"].includes(ext);
  };

  const openPreview = async (f) => {
    // Open the preview dialog immediately so the user sees a spinner
    setPreviewOpen(true);
    setPreviewFile({ ...f, objectUrl: null });
    setPreviewLoading(true);

    try {
      // Prefer fetching via the app http client so Authorization header is sent
      let src = null;
      const full = getFullUrl(f);

      // Try fetching by full URL when it points to our API or looks protected
      if (full) {
        try {
          const { blob } = await getBlobFromUrl(full);
          src = URL.createObjectURL(blob);
        } catch (e) {
          // ignore and try fallback
        }
      }

      // Fallback: try fetching by fileName route
      if (!src && (f.fileName || f.FileName || f.fileId || f.FileId)) {
        try {
          const { blob } = await getBlobByFileName(f.fileName || f.FileName || f.fileId || f.FileId);
          src = URL.createObjectURL(blob);
        } catch (e) {
          // ignore
        }
      }

      // Final fallback: use raw URL if available
      if (!src) src = full;

      // Update preview file with object URL (or raw url if that's all we have)
      setPreviewFile((prev) => ({ ...prev, objectUrl: src }));
    } catch (err) {
      console.error("Failed to load preview", err);
      setSnackbar({ open: true, message: "❌ Unable to load file preview", severity: "error" });
      // Close the dialog if loading failed and no useful preview is available
      // (keep it open only if we have something to show)
      setTimeout(() => {
        setPreviewOpen(false);
        setPreviewFile(null);
      }, 500);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    // revoke object URL if we created one
    if (previewFile && previewFile.objectUrl && previewFile.objectUrl.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(previewFile.objectUrl);
      } catch (e) {}
    }
    setPreviewFile(null);
  };

  // Build absolute URL for files (prefix with apiUrl if relative)
  const getFullUrl = (f) => {
    const raw = f?.url || f?.Url || "";
    if (!raw) return null;
    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
    // If it's root-relative (starts with /) or relative, prefix with apiUrl
    const base = (apiUrl || "").replace(/\/$/, "");
    if (!base) return raw;
    if (raw.startsWith("/")) return `${base}${raw}`;
    return `${base}/${raw}`;
  };

  const openFile = async (f) => {
    const full = getFullUrl(f);
    if (!full && !(f.fileName || f.FileName)) {
      setSnackbar({ open: true, message: "❌ No file URL available", severity: "error" });
      return;
    }

    try {
      // Try to fetch through the app client (adds Authorization header)
      const { blob, headers } = full ? await getBlobFromUrl(full) : await getBlobByFileName(f.fileName || f.FileName);

      // Try to determine filename from response headers or file object
      let filename = f.fileName || f.FileName || "file";
      const cd = headers && (headers['content-disposition'] || headers['Content-Disposition']);
      if (cd) {
        const m1 = /filename\*=UTF-8''(.+)$/.exec(cd);
        const m2 = /filename="(.+)"/.exec(cd);
        const m3 = /filename=(.+);?/.exec(cd);
        const m = m1 || m2 || m3;
        if (m && m[1]) {
          try { filename = decodeURIComponent(m[1]); } catch(e) { filename = m[1]; }
        }
      }

      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || 'file';
      // For images/PDFs we still want to allow opening in new tab when user chooses 'Open' action.
      // Use click to trigger download; also open in new tab for preview-like behavior.
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => { try { URL.revokeObjectURL(blobUrl); } catch (e) {} }, 5000);
    } catch (err) {
      // If fetching with auth fails, fallback to opening the direct URL in a new tab
      console.warn('Auth fetch failed, falling back to direct URL open', err);
      try {
        window.open(full, "_blank", "noopener,noreferrer");
      } catch (e) {
        window.location.href = full;
      }
    }
  };

  const handleWalletChange = (event, value) => {
    setSelectedWallet(value);
    setCreateFormData((prev) => ({
      ...prev,
      walletId: value?.id || "",
    }));
  };

 const handleCreateCashOut = async (data) => {
  const { driverIds, amount, paymentMethod, files } = data;

  if (!driverIds?.length || !amount || !paymentMethod) {
    setSnackbar({
      open: true,
      message: "Please fill in all required fields",
      severity: "warning",
    });
    return;
  }

  setDialogLoading(true);
    try {
    // Client-side validation: fetch cash out rule and apply checks so we can
    // show friendly messages instead of letting the server return 400.
    const rule = await getCashOutRule().catch(() => null);
    if (!rule) {
      setSnackbar({ open: true, message: "❌ Cash out rules are not configured. Contact admin.", severity: "error" });
      setDialogLoading(false);
      return;
    }

    const min = Number(rule.MinAmount ?? rule.minAmount ?? rule.min ?? NaN);
    const max = Number(rule.MaxAmount ?? rule.maxAmount ?? rule.max ?? NaN);
    if (!Number.isNaN(min) && !Number.isNaN(max)) {
      if (Number(amount) < min || Number(amount) > max) {
        setSnackbar({ open: true, message: `⚠️ Cash out amount must be between ${min} and ${max}.`, severity: "warning" });
        setDialogLoading(false);
        return;
      }
    }

    const getDriverLabel = (id) => {
      const drv = drivers.find((d) => String(d.id) === String(id));
      return drv?.fullName || drv?.name || drv?.full_name || drv?.fullNameEn || "This driver";
    };

    // For each driver, check cutoff and wallet balance when applicable
    for (const drvId of driverIds) {
      try {
        // fetch last request for this driver
        const lastResp = await getAllCashOutRequests({ pageNumber: 1, pageSize: 1, driverId: String(drvId) }).catch(() => null);
        const lastItem = lastResp && Array.isArray(lastResp.items) && lastResp.items.length > 0 ? lastResp.items[0] : null;
        if (lastItem && (rule.CutoffDays ?? rule.cutoffDays ?? rule.cutoff)) {
          const cutoff = Number(rule.CutoffDays ?? rule.cutoffDays ?? rule.cutoff ?? 0);
          const lastDate = new Date(lastItem.creationDate || lastItem.creationAt || lastItem.CreationDate || lastItem.creationDateUTC || lastItem.creationDate || null);
          if (!Number.isNaN(lastDate.getTime())) {
            const days = (Date.now() - lastDate.getTime()) / (1000 * 60 * 60 * 24);
            if (days < cutoff) {
              setSnackbar({ open: true, message: `⏳ Cash out allowed every ${cutoff} days.`, severity: "warning" });
              setDialogLoading(false);
              return;
            }
          }
        }

        // check wallet balance for this driver
        const walletsResp = await getAllWallets({ pageNumber: 1, pageSize: 1, driverId: String(drvId) }).catch(() => null);
        let wallet = null;
        if (walletsResp) {
          if (Array.isArray(walletsResp)) wallet = walletsResp[0];
          else if (Array.isArray(walletsResp.items)) wallet = walletsResp.items[0];
          else if (Array.isArray(walletsResp.page)) wallet = walletsResp.page[0];
          else if (Array.isArray(walletsResp.list)) wallet = walletsResp.list[0];
        }
        if (!wallet) {
          const driverLabel = getDriverLabel(drvId);
          setSnackbar({ open: true, message: `⚠️ ${driverLabel} has no wallet.`, severity: "warning" });
          setDialogLoading(false);
          return;
        }

        const balance = wallet ? (wallet.balance ?? wallet.Balance ?? wallet.walletBalance ?? null) : null;
        if (balance != null && !Number.isNaN(Number(balance))) {
          if (Number(balance) < Number(amount)) {
            setSnackbar({ open: true, message: "💳 Insufficient wallet balance. Driver cannot cash out this amount.", severity: "warning" });
            setDialogLoading(false);
            return;
          }
        }
      } catch (e) {
        // ignore per-driver check errors; continue to next driver
      }
    }
    const submitData = {
      driverIds,
      amount,
      paymentMethod,
      createdByAdmin: true,
      // create requests as Pending by default (TransactionStatusEnum.Pending = 1)
      status: 1,
      files,
    };

      // upload progress handler
      const onUploadProgress = (progressEvent) => {
        if (!progressEvent || !progressEvent.total) return;
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setCreateUploadProgress(percent);
      };

      await createCashOutRequest(submitData, onUploadProgress);

      setCreateUploadProgress(null);

    setSnackbar({
      open: true,
      message: "✅ Cash out request created successfully!",
      severity: "success",
    });
    handleCloseDialog();
    fetchRequests();
  } catch (err) {
    console.error("Failed to create cash out request:", err);
    const { code, message: backendMessage } = extractErrorInfo(err);
    // build user-friendly message (may fetch rule values)
    let display = await buildCashOutUserMessage(code, backendMessage).catch(() => null);
    if (!display) display = mapCashOutError(code, backendMessage) || (backendMessage ? backendMessage : null);

    // Stronger fallback: prefer any readable string from the raw response body
    if (!display) {
      const raw = err?.response?.data;
      if (raw) {
        if (typeof raw === "string") display = raw;
        else if (typeof raw === "object") {
          // pick the first stringy value longer than 3 chars
          const vals = Object.values(raw).flatMap((v) => (Array.isArray(v) ? v : [v]));
          const found = vals.find((v) => typeof v === "string" && v.trim().length > 3);
          if (found) display = found;
          else {
            try {
              display = JSON.stringify(raw).slice(0, 400);
            } catch (e) {
              display = String(raw);
            }
          }
        }
      }
    }

    if (!display) display = err?.message || "Failed to create cash out request";

    // In dev, print the raw response to console for debugging so you can
    // copy the exact response body if needed.
    if (process.env.NODE_ENV !== 'production') {
      try {
        // eslint-disable-next-line no-console
        console.debug('CashOut create error response raw:', err?.response?.data || err);
      } catch (e) {}
    }

    setSnackbar({ open: true, message: display, severity: "error" });
  } finally {
    setDialogLoading(false);
  }
};

  // Helper: extract backend error code and message from typical ServiceException shapes
  const extractErrorInfo = (err) => {
    const data = err?.response?.data || {};
    // Common shapes for codes
    const code = data.statusCode || data.code || data.errorCode || data?.data?.statusCode || data?.status || null;

    // Try multiple places for a human-friendly message
    const tryPaths = [
      () => data.message,
      () => data.error,
      () => data.detail,
      () => data.title,
      () => data.message_en,
      () => data.message_ar,
      () => data.data && data.data.message,
      () => data.data && data.data.error,
      () => data.data && data.data.detail,
      () => (Array.isArray(data.errors) && data.errors[0] && (data.errors[0].message || data.errors[0].error)) || null,
      () => (data.errors && data.errors.message) || null,
      () => (data.data && Array.isArray(data.data.errors) && data.data.errors[0] && (data.data.errors[0].message || data.data.errors[0].error)) || null,
      () => err?.message,
      () => err?.response?.statusText,
    ];

    let message = null;
    for (const fn of tryPaths) {
      try {
        const v = fn();
        if (v && typeof v === "string" && v.trim().length > 0) {
          // accept readable strings; prefer longer, human messages
          const tokenLike = /^[A-Z0-9_]+$/i.test(v.trim()) && v.trim().length <= 40;
          if (!tokenLike) {
            message = v.trim();
            break;
          }
          if (!message && String(v).trim() !== String(code).trim()) {
            message = v.trim();
          }
        }
      } catch (e) {
        // ignore
      }
    }

    // Deep-scan fallback: recursively search object for any readable string
    if (!message) {
      const visited = new Set();
      const deepSearch = (obj) => {
        if (!obj || visited.has(obj)) return null;
        visited.add(obj);
        if (typeof obj === "string") {
          const s = obj.trim();
          if (s.length > 6 && s.toLowerCase().indexOf(String(code || "").toLowerCase()) === -1) return s;
          return null;
        }
        if (typeof obj === "number" || typeof obj === "boolean") return null;
        if (Array.isArray(obj)) {
          for (const it of obj) {
            const found = deepSearch(it);
            if (found) return found;
          }
          return null;
        }
        if (typeof obj === "object") {
          for (const k of Object.keys(obj)) {
            try {
              const found = deepSearch(obj[k]);
              if (found) return found;
            } catch (e) {
              // ignore
            }
          }
        }
        return null;
      };

      try {
        message = deepSearch(data) || deepSearch(err?.response) || null;
      } catch (e) {
        // ignore
      }
    }

    return { code: code ? String(code) : null, message };
  };

  // Map known backend error codes to user-facing messages
  const mapCashOutError = (code, backendMessage) => {
    if (!code && !backendMessage) return null;
    const normalized = code ? String(code) : "";
    // If backend included a human message, prefer that for InvalidAmount/CashOutNotAllowed so user sees details
    const map = {
      CashOutRuleNotFound: () => "❌ Cash out rules are not configured. Contact admin.",
      InvalidAmount: () => (backendMessage ? `⚠️ ${backendMessage}` : "⚠️ Invalid amount."),
      CashOutNotAllowed: () => (backendMessage ? `⏳ ${backendMessage}` : "⏳ Cash out not allowed at this time."),
      InsufficientWalletBalance: () => "💳 Insufficient wallet balance. Driver cannot cash out this amount.",
    };

    if (map[normalized]) return map[normalized]();

    // Also handle numeric status codes if backend uses them (try parseInt)
    const num = Number(normalized);
    if (!Number.isNaN(num)) {
      switch (num) {
        // fallback numeric mappings could be added here if known
        default:
          return backendMessage || null;
      }
    }

    // as last resort, if the backend message contains one of the known tokens, use mapped message
    if (backendMessage) {
      if (backendMessage.includes("Cash out rules")) return "❌ Cash out rules are not configured. Contact admin.";
      if (backendMessage.includes("between")) return `⚠️ ${backendMessage}`;
      if (backendMessage.includes("every")) return `⏳ ${backendMessage}`;
      if (backendMessage.includes("Insufficient")) return "💳 Insufficient wallet balance. Driver cannot cash out this amount.";
      if (backendMessage.includes("Nullable object must have a value") || backendMessage.includes("Nullable object must have a value.")) return "❌ Missing required value. Ensure payment method is selected and try again.";
    }

    return null;
  };

  // Build a user-facing message; if backend doesn't include details for
  // InvalidAmount or CashOutNotAllowed, fetch the configured rule to fill values.
  const buildCashOutUserMessage = async (code, backendMessage) => {
    // prefer map result if it returns a message
    const mapped = mapCashOutError(code, backendMessage);
    if (mapped) return mapped;

    // If the backend indicated InvalidAmount or CashOutNotAllowed but didn't
    // include the human message, fetch the rule and construct it here.
    const key = code ? String(code) : null;
    if (key === "InvalidAmount") {
      const rule = await getCashOutRule().catch(() => null);
      if (rule && (typeof rule.MinAmount !== 'undefined' || typeof rule.minAmount !== 'undefined')) {
        const min = rule.MinAmount ?? rule.minAmount ?? rule.min ?? ""
        const max = rule.MaxAmount ?? rule.maxAmount ?? rule.max ?? "";
        return `⚠️ Cash out amount must be between ${min} and ${max}.`;
      }
      // fallback message
      return backendMessage ? `⚠️ ${backendMessage}` : "⚠️ Invalid amount.";
    }

    if (key === "CashOutNotAllowed") {
      const rule = await getCashOutRule().catch(() => null);
      if (rule && (typeof rule.CutoffDays !== 'undefined' || typeof rule.cutoffDays !== 'undefined')) {
        const days = rule.CutoffDays ?? rule.cutoffDays ?? rule.cutoff ?? "";
        return `⏳ Cash out allowed every ${days} days.`;
      }
      return backendMessage ? `⏳ ${backendMessage}` : "⏳ Cash out not allowed at this time.";
    }

    // If code is CashOutRuleNotFound, map already handles it; otherwise return null
    return null;
  };


  return (
    <Box sx={{ p: "2rem" }}>
      {/* Header */}
      <Box sx={{ mb: "2rem" }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: "0.25rem", color: "#2A3467" }}>
          Cash Out Requests
        </Typography>
        <Typography variant="body2" sx={{ color: "#2A3467", fontWeight: 600 }}>
          Manage and process cash out requests from drivers
        </Typography>
      </Box>

      {/* Create Button moved below filters to align with Wallet Transactions layout */}

      {/* Filters */}
      <Paper sx={{ padding: "1.5rem", marginBottom: "2rem" }}>
        <Typography variant="h6" sx={{ marginBottom: "1rem" }}>Filters</Typography>
        <Stack spacing={2}>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 2 }}>
            <Autocomplete
              size="small"
              fullWidth
              options={drivers}
              getOptionLabel={(option) => {
                if (!option) return "";
                return `${option.fullName || "Unknown"} ${option.phone ? `(${option.phone})` : ""}`;
              }}
              isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
              inputValue={driverInputValue}
              value={
                // Prefer the selectedDriver object when available so the
                // Autocomplete reliably shows the selection even if the
                // `drivers` options are updated by a search.
                selectedDriver || (filters.driverId === "" ? null : drivers.find((d) => d.id.toString() === filters.driverId) || null)
              }
              clearOnBlur={false}
              onChange={(event, newValue) => {
                try { console.debug('CashOut driver filter onChange, newValue=', newValue); } catch(e){}
                if (newValue) {
                  // keep the primitive filter in sync
                  setFilters((prev) => ({ ...prev, driverId: newValue.id.toString() }));
                  // keep the selected object so Autocomplete can render it even
                  // if the current options are replaced by a later search
                  setSelectedDriver(newValue);
                  // keep the input showing the selected driver's label
                  setDriverInputValue(`${newValue.fullName || "Unknown"}${newValue.phone ? ` (${newValue.phone})` : ""}`);
                  // ensure the selected option is present in the drivers list
                  setDrivers((prev) => {
                    if (!prev || !Array.isArray(prev)) return [newValue];
                    if (prev.some((d) => String(d.id) === String(newValue.id))) return prev;
                    return [newValue, ...prev];
                  });
                } else {
                  setFilters((prev) => ({ ...prev, driverId: "" }));
                  setSelectedDriver(null);
                  setDriverInputValue("");
                }
                // keep user's typed input intact (don't clear)
              }}
              onInputChange={(event, newInputValue) => {
                setDriverInputValue(newInputValue);
                try { if (newInputValue && newInputValue.trim().length > 0) searchDrivers(newInputValue); } catch (e) {}
              }}
              // allow the dropdown to open on focus and select immediately
              openOnFocus={true}
              autoSelect={true}
              filterOptions={(options, state) => {
                // when input is empty, show all options
                if (!state.inputValue || state.inputValue.trim() === "") {
                  return options;
                }
                const inputLower = state.inputValue.toLowerCase().trim();
                return options
                  .filter((option) => {
                    const name = (option.fullName || "").toLowerCase();
                    const phone = (option.phone || "").toLowerCase();
                    const idStr = (option.id || "").toString().toLowerCase();
                    const nameMatch = name.includes(inputLower);
                    const phoneMatch = phone.includes(inputLower);
                    const idMatch = idStr.includes(inputLower);
                    return nameMatch || phoneMatch || idMatch;
                  })
                  .sort((a, b) => {
                    const aName = (a.fullName || "").toLowerCase();
                    const bName = (b.fullName || "").toLowerCase();
                    const aStartsWith = aName.startsWith(inputLower);
                    const bStartsWith = bName.startsWith(inputLower);
                    if (aStartsWith && !bStartsWith) return -1;
                    if (!aStartsWith && bStartsWith) return 1;
                    return aName.localeCompare(bName);
                  });
              }}
              loading={driversLoading}
              disabled={driversLoading}
              renderInput={(params) => (
                <TextField {...params} label="Driver" placeholder="Search driver..." margin="none" />
              )}
              noOptionsText="No drivers found"
            />
            

            <FormControl size="small" fullWidth variant="outlined">
              <InputLabel id="status-select-label">Status</InputLabel>
              <Select
                labelId="status-select-label"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                {Object.entries(cashOutStatusEnumLocal).map(([key, label]) => (
                  <MenuItem key={key} value={key} style={{ color: statusColors[key] }}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="From"
              name="fromDate"
              type="date"
              value={filters.fromDate}
              onChange={handleFilterChange}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="To"
              name="toDate"
              type="date"
              value={filters.toDate}
              onChange={handleFilterChange}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              sx={{ backgroundColor: "#2A3467", height: "40px" }}
            >
              Apply Filters
            </Button>
            <Button
              variant="outlined"
              onClick={handleResetFilters}
              sx={{ borderColor: "#2A3467", color: "#2A3467", height: "40px" }}
            >
              Reset
            </Button>
          </Box>
        </Stack>
      </Paper>

      {/* Create Button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", marginBottom: "1rem" }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
          sx={{ backgroundColor: "#2A3467" }}
        >
          Create Cash Out Request
        </Button>
      </Box>

      {/* Table Section */}
      <Paper sx={{ marginTop: "1rem", overflowX: "auto" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: "3rem" }}>
            <CircularProgress />
          </Box>
        ) : requests.length === 0 ? (
          <Box sx={{ p: "2rem", textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "#999" }}>
              No cash out requests found
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Driver Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Driver Wallet Balance</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    Amount (JOD)
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Files</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Created Date</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((req) => (
                  <TableRow key={req.id} sx={{ "&:hover": { backgroundColor: "#fafafa" } }}>
                    <TableCell>{req.driver?.fullName || req.driverName || "Unknown"}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {
                        (() => {
                          const balance =
                            req.driver?.walletBalance ??
                            req.driverWalletBalance ??
                            req.walletTransaction?.wallet?.balance ??
                            req.balance ??
                            null;
                          return balance !== null && !Number.isNaN(Number(balance))
                            ? `${Number(balance).toFixed(2)} JOD`
                            : "N/A";
                        })()
                      }
                    </TableCell>
                    <TableCell  sx={{ fontWeight: 600 }}>
                      {req.amount?.toFixed(2)} JOD
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={cashOutStatusEnumLocal[req.status] || "Unknown"}
                        sx={{
                          backgroundColor: statusColors[req.status] || "#ccc",
                          color: "white",
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {req.files && req.files.length > 0 ? (
                        <Tooltip title={req.files.map((f) => f.fileName || f.FileName || f.fileId || f.FileId || "file").join("\n")}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              // Open preview dialog for the first attached file (do not open a new tab)
                              if (req.files && req.files[0]) {
                                openPreview(req.files[0]);
                              } else {
                                setSnackbar({ open: true, message: "❌ No file available to preview", severity: "error" });
                              }
                            }}
                            startIcon={<InsertDriveFileIcon />}
                          >
                            {req.files.length}
                          </Button>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" sx={{ color: "#999" }}>-</Typography>
                      )}
                    </TableCell>
                    <TableCell>{new Date(req.creationDate).toLocaleDateString()}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Button
                          size="small"
                          variant="text"
                          sx={{ color: "#2A3467" }}
                          onClick={() => handleOpenViewDialog(req)}
                        >
                          View
                        </Button>
                        {req.status === 1 && (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              sx={{
                                backgroundColor: "#4CAF50",
                                color: "white",
                                fontSize: "0.75rem",
                              }}
                              onClick={() => handleOpenApproveDialog(req)}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              sx={{
                                backgroundColor: "#F44336",
                                color: "white",
                                fontSize: "0.75rem",
                              }}
                              onClick={() => handleOpenRejectDialog(req)}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      

      {/* Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        {dialogMode === "view" && (
          <>
            <DialogTitle>Request Details</DialogTitle>
            <DialogContent sx={{ pt: "1rem" }}>
              {selectedRequest && (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#999" }}>
                      Driver Wallet Balance
                    </Typography>
                    <Typography variant="body2">
                      {
                        (() => {
                          const balance =
                            selectedRequest?.driver?.walletBalance ??
                            selectedRequest?.driverWalletBalance ??
                            selectedRequest?.walletTransaction?.wallet?.balance ??
                            selectedRequest?.balance ??
                            null;
                          return balance !== null && !Number.isNaN(Number(balance))
                            ? `${Number(balance).toFixed(2)} JOD`
                            : "N/A";
                        })()
                      }
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" sx={{ color: "#999" }}>
                      Driver Name
                    </Typography>
                    <Typography variant="body2">{selectedRequest.driver?.fullName || selectedRequest.driverName || "Unknown"}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" sx={{ color: "#999" }}>
                      Amount (JOD)
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selectedRequest.amount?.toFixed(2)} JOD
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" sx={{ color: "#999" }}>
                      Status
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          px: 1.25,
                          py: 0.45,
                          borderRadius: "16px",
                          backgroundColor: statusColors[selectedRequest.status] || "#ccc",
                          color: "white",
                          fontWeight: 700,
                          textTransform: "capitalize",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                        }}
                      >
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "rgba(255,255,255,0.9)", mr: 1 }} />
                        {cashOutStatusEnumLocal[selectedRequest.status] || "Unknown"}
                      </Box>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" sx={{ color: "#999" }}>
                      Created Date
                    </Typography>
                    <Typography variant="body2">
                      {new Date(selectedRequest.creationDate).toLocaleString()}
                    </Typography>
                  </Box>

                  {selectedRequest.walletTransaction && (
                    <Box>
                      <Typography variant="caption" sx={{ color: "#999" }}>
                        Wallet Transaction ID
                      </Typography>
                      <Typography variant="body2">{selectedRequest.walletTransaction?.id}</Typography>
                    </Box>
                  )}

                  {selectedRequest.adminReason && (
                    <Box>
                      <Typography variant="caption" sx={{ color: "#999" }}>
                        Rejection Reason
                      </Typography>
                      <Typography variant="body2">{selectedRequest.adminReason}</Typography>
                    </Box>
                  )}

                  {selectedRequest.files && selectedRequest.files.length > 0 && (
                    <Box>
                      <Typography variant="caption" sx={{ color: "#999" }}>
                        Attached Files
                      </Typography>
                      <List dense sx={{ mt: 1 }}>
                        {selectedRequest.files.map((f) => {
                          const previewable = isPreviewable(f);
                          return (
                            <ListItem
                              key={f.id || f.Id || f.fileId || f.FileId}
                              secondaryAction={
                                <Stack direction="row" spacing={1}>
                                  {previewable && (
                                    <Button size="small" variant="text" onClick={() => openPreview(f)}>
                                      Preview
                                    </Button>
                                  )}

                                  <Button
                                    onClick={() => openFile(f)}
                                    size="small"
                                    variant="outlined"
                                    startIcon={<InsertDriveFileIcon />}
                                  >
                                    Download
                                  </Button> 
                                </Stack>
                              }
                              button={previewable}
                              onClick={() => previewable && openPreview(f)}
                            >
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: "transparent" }}>{getFileIcon(f.fileName || f.FileName)}</Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={f.fileName || f.FileName || f.fileId || f.FileId || "File"}
                                secondary={`${(f.size || f.Size || 0) ? formatBytes(Number(f.size || f.Size || 0)) : ""}${f.fileType ? ` • ${f.fileType}` : ""}`}
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    </Box>
                  )}
                </Stack>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Close</Button>
            </DialogActions>
          </>
        )}

        {dialogMode === "approve" && (
          <>
            <DialogTitle>Approve Cash Out Request</DialogTitle>
            <DialogContent sx={{ pt: "1rem" }}>
              {selectedRequest && (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {selectedRequest.driver?.fullName || selectedRequest.driverName || "Unknown"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      Amount: {selectedRequest.amount?.toFixed(2)} JOD
                    </Typography>
                  </Box>

                  <FormControl fullWidth error={!approvePaymentMethod && dialogMode === "approve"}>
                      <InputLabel required>Payment Method</InputLabel>
                      <Select
                        value={approvePaymentMethod}
                        label="Payment Method"
                        onChange={(e) => setApprovePaymentMethod(e.target.value)}
                      >
                      <MenuItem value="">Not specified</MenuItem>
                      {Object.entries(paymentMethodEnum).map(([key, label]) => (
                        <MenuItem key={key} value={key}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                    </FormControl>

                  {/* Admin Evidence File Upload */}
                  <FileUploader
                    files={approveFiles}
                    setFiles={setApproveFiles}
                    accept={"image/*,application/pdf"}
                    multiple={true}
                    maxSizeMB={20}
                    label={"Attach admin evidence (optional, images & PDF)"}
                    uploadProgress={approveUploadProgress}
                  />
                </Stack>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} disabled={dialogLoading}>
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (!approvePaymentMethod) {
                    setSnackbar({ open: true, message: "⚠️ Please select a payment method before approving.", severity: "warning" });
                    return;
                  }
                  await handleApprove();
                }}
                variant="contained"
                sx={{ backgroundColor: "#4CAF50" }}
                disabled={dialogLoading}
              >
                {dialogLoading ? "Approving..." : "Approve"}
              </Button>
            </DialogActions>
          </>
        )}

        {dialogMode === "reject" && (
          <>
            <DialogTitle>Reject Cash Out Request</DialogTitle>
            <DialogContent sx={{ pt: "1rem" }}>
              {selectedRequest && (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {selectedRequest.driver?.fullName || selectedRequest.driverName || "Unknown"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      Amount: {selectedRequest.amount?.toFixed(2)} JOD
                    </Typography>
                  </Box>

                  <TextField
                    label="Rejection Reason"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    multiline
                    rows={4}
                    fullWidth
                    placeholder="Provide a reason for rejection..."
                    required
                  />
                </Stack>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} disabled={dialogLoading}>
                Cancel
              </Button>
              <Button
                onClick={handleReject}
                variant="contained"
                sx={{ backgroundColor: "#F44336" }}
                disabled={dialogLoading}
              >
                {dialogLoading ? "Rejecting..." : "Reject"}
              </Button>
            </DialogActions>
          </>
        )}

        {dialogMode === "create" && (
          <>
            <DialogTitle>Create Cash Out Request</DialogTitle>
            <DialogContent sx={{ pt: "1rem" }}>
              <CashOutRequestForm
                initialData={{ driverIds: [] }}
                onSubmit={handleCreateCashOut}
                isLoading={dialogLoading}
                drivers={drivers}
                driversLoading={driversLoading}
                files={createFormData.files}
                setFiles={(files) => setCreateFormData((prev) => ({ ...prev, files }))}
                uploadProgress={createUploadProgress}
              />
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Preview Dialog for attached files */}
      <Dialog open={previewOpen} onClose={closePreview} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {previewFile?.fileName || previewFile?.FileName || "Preview"}
          <Box>
            <Button
              onClick={() => openFile(previewFile)}
              size="small"
              variant="outlined"
              sx={{ mr: 1 }}
            >
              Download
            </Button>
            <Button onClick={closePreview} size="small">Close</Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {previewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress />
            </Box>
          ) : previewFile ? (
            (() => {
              const ext = getExtension(previewFile.fileName || previewFile.FileName || previewFile.fileId || previewFile.FileId || "");
              const src = previewFile.objectUrl || getFullUrl(previewFile);
              if (["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext)) {
                return (
                  <Box sx={{ textAlign: "center" }}>
                    <img src={src} alt={previewFile.fileName || previewFile.FileName} style={{ maxWidth: "100%", maxHeight: "70vh" }} />
                  </Box>
                );
              }

              if (ext === "pdf") {
                return (
                  <iframe
                    title={previewFile.fileName || previewFile.FileName}
                    src={src}
                    style={{ width: "100%", height: "70vh", border: "none" }}
                  />
                );
              }

              return (
                <Box>
                  <Typography>Preview not available for this file type.</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>{previewFile.fileName || previewFile.FileName}</Typography>
                </Box>
              );
            })()
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CashOutRequestPage;
